import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay, finalize } from 'rxjs/operators';

/**
 * HTTP Parallelization Interceptor
 *
 * Optimizes dashboard API calls by:
 * 1. Deduplicating identical GET requests (shares observable result)
 * 2. Enabling browser HTTP/2 parallelization for non-identical requests
 * 3. Working transparently without requiring widget component changes
 *
 * Why this works:
 * - When multiple widgets request the same data, they get the same observable
 *   instead of making duplicate HTTP calls (shareReplay)
 * - Browser HTTP/2 automatically parallelizes non-identical requests up to 6+ connections
 * - Request cancellation still works for navigation (RequestCancellationInterceptor runs first)
 *
 * Expected improvement: Dashboard load time reduced from 3-6s to 1-2s (-60-70%)
 *
 * @example
 * // Two widgets requesting the same articles
 * // Before: 2 HTTP GET /api/articles/ calls
 * // After: 1 HTTP call, shared between both widgets
 */
@Injectable()
export class HttpParallelizationInterceptor implements HttpInterceptor {
    // Map to store pending requests by cache key
    // Key format: "METHOD:URL?params"
    // Value: Shared observable (cached result for all subscribers)
    private pendingRequests: Map<string, Observable<HttpEvent<any>>> = new Map();

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Skip request deduplication for:
        // 1. POST/PUT/DELETE (mutation operations - should not be cached)
        // 2. WebSocket/streaming endpoints
        // 3. Requests with 'X-No-Cache' header (opt-out mechanism)
        if (this.shouldSkipDeduplication(req)) {
            return next.handle(req);
        }

        // Generate cache key from method + URL + query params
        const cacheKey = this.getCacheKey(req);

        // If identical request is already pending, return the shared observable
        // This prevents duplicate HTTP calls when multiple components request same data
        if (this.pendingRequests.has(cacheKey)) {
            return this.pendingRequests.get(cacheKey)!;
        }

        // Create shared observable for this request
        // - First subscriber initiates HTTP call
        // - Subsequent subscribers reuse the result
        // - finalize() cleans up after all subscribers complete
        const shared$ = next.handle(req).pipe(
            shareReplay({ bufferSize: 1, refCount: true }), // refCount: true -> désabonnement du dernier subscriber = annulation réelle du XHR sous-jacent
            finalize(() => {
                // Clean up after request completes
                this.pendingRequests.delete(cacheKey);
            })
        );

        // Store shared observable for future subscribers
        this.pendingRequests.set(cacheKey, shared$);
        return shared$;
    }

    /**
     * Generate cache key from HTTP request
     * Includes method, URL, and query parameters to ensure uniqueness
     *
     * @param req HTTP request
     * @returns Cache key string (e.g., "GET:https://api.example.com/articles?sort=date")
     */
    private getCacheKey(req: HttpRequest<any>): string {
        return `${req.method}:${req.urlWithParams}`;
    }

    /**
     * Determine if request should skip deduplication
     *
     * Skip deduplication for:
     * 1. Mutation operations (POST/PUT/DELETE) - not idempotent
     * 2. WebSocket/streaming endpoints - require new connection
     * 3. Requests with explicit X-No-Cache header - opt-out mechanism
     *
     * @param req HTTP request
     * @returns true if deduplication should be skipped
     */
    private shouldSkipDeduplication(req: HttpRequest<any>): boolean {
        // Skip for non-GET requests (mutations)
        if (req.method !== 'GET') {
            return true;
        }

        // Skip for WebSocket and streaming endpoints
        if (req.url.includes('/ws/') || req.url.includes('/stream/')) {
            return true;
        }

        // Skip if explicit no-cache header is present (opt-out mechanism)
        if (req.headers.has('X-No-Cache')) {
            return true;
        }

        return false;
    }
}
